/**
 * Company List Components - List item and empty states
 */
import { ChevronRight, Building2, Check } from "lucide-react";
import { formatDateShort, formatTime } from "../../../utils/dateTime";

// Activity Type Labels
export const ACTIVITY_TYPE_LABELS = {
  "POZIV": "Poziv",
  "DOGOVOREN_SASTANAK": "Dogovoren sastanak",
  "PONUDA_POSLANA": "Ponuda",
  "AKVIZICIJA": "Akvizicija"
};

// Status Badge Component
export const StatusBadge = ({ status }) => {
  const statusConfig = {
    OVERDUE: { label: "Zakašnjelo", bg: "bg-red-100", text: "text-red-700" },
    TODAY: { label: "Danas", bg: "bg-green-100", text: "text-green-700" },
    SCHEDULED: { label: "Zakazano", bg: "bg-purple-100", text: "text-purple-700" },
    FOLLOW_UP: { label: "Follow-up", bg: "bg-amber-100", text: "text-amber-700" },
    NO_NEXT: { label: "Bez datuma", bg: "bg-gray-100", text: "text-gray-600" },
    NEW: { label: "Novi", bg: "bg-blue-100", text: "text-blue-700" },
    CLOSED: { label: "Zatvoreno", bg: "bg-gray-100", text: "text-gray-600" },
    ACQUISITION: { label: "Akvizicija", bg: "bg-emerald-100", text: "text-emerald-700" },
    NOT_INTERESTED: { label: "Nije zainteresiran", bg: "bg-red-100", text: "text-red-700" },
    DO_NOT_CONTACT: { label: "Ne kontaktirati", bg: "bg-red-100", text: "text-red-700" },
    MEETING_SCHEDULED: { label: "Zakazano", bg: "bg-purple-100", text: "text-purple-700" }
  };
  const config = statusConfig[status] || statusConfig.NEW;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Company List Item
export const CompanyListItem = ({ company, onClick }) => {
  const lastActivity = company.last_activity;
  const formatActivityDate = (isoString) => formatDateShort(isoString) + ' ' + formatTime(isoString);
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:border-blue-100 transition-colors cursor-pointer active:scale-[0.99]"
      onClick={onClick}
      data-testid={`company-item-${company.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-gray-900 truncate">{company.name}</p>
            <StatusBadge status={company.status} />
          </div>
          {company.oib && <p className="text-xs text-gray-400 mb-1">ID: {company.oib}</p>}
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <span>{company.city}</span>
            <span className="text-gray-300">•</span>
            <span className="truncate">{company.industry}</span>
            {company.source && (
              <>
                <span className="text-gray-300">•</span>
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  company.source === 'AI pretraga' ? 'bg-purple-50 text-purple-600' :
                  company.source === 'JN' ? 'bg-blue-50 text-blue-600' :
                  company.source === 'Import' ? 'bg-amber-50 text-amber-600' :
                  'bg-gray-50 text-gray-500'
                }`}>{company.source}</span>
              </>
            )}
          </div>
          {company.director_name && (
            <p className="text-xs text-gray-500 mb-1">
              <span className="text-gray-400">Direktor:</span> {company.director_name}
            </p>
          )}
          {lastActivity && (
            <p className="text-xs text-gray-500 mb-1">
              <span className="text-gray-400">Zadnje:</span> {ACTIVITY_TYPE_LABELS[lastActivity.type] || lastActivity.type}
              <span className="text-gray-300 mx-1">•</span>
              {formatActivityDate(lastActivity.created_at)}
            </p>
          )}
          <p className="text-xs">
            <span className="text-gray-400">Sljedeće:</span>{" "}
            <span className={company.status === 'OVERDUE' ? 'text-red-600 font-medium' : company.status === 'TODAY' ? 'text-green-600 font-medium' : 'text-gray-600'}>
              {company.next_label}
            </span>
          </p>
        </div>
        <button className="flex-shrink-0 text-gray-400 hover:text-[#1B3F8F] p-1 mt-1">
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

// Empty State Component
export const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center animate-fadeIn">
    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <Icon size={36} className="text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 max-w-xs">{description}</p>
  </div>
);

// Company List with server-side pagination
export const CompanyList = ({ 
  companies,
  totalCount,
  currentPage,
  totalPages,
  loading, 
  searchQuery, 
  activeTab, 
  hasMore, 
  remainingCount,
  onCompanyClick,
  onLoadMore 
}) => {
  if (loading && companies.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Učitavanje...</div>
      </div>
    );
  }
  
  if (companies.length === 0) {
    if (searchQuery) {
      return <EmptyState icon={Building2} title="Nema rezultata" description={`Nema firmi koje odgovaraju pretrazi "${searchQuery}"`} />;
    }
    if (activeTab === "active") {
      return <EmptyState icon={Check} title="Nema firmi za obradu" description="Sve firme su obrađene. Odličan posao!" />;
    }
    return <EmptyState icon={Building2} title="Nema firmi" description="Trenutno nema firmi za prikaz." />;
  }
  
  return (
    <div className="space-y-3 animate-fadeIn">
      <p className="text-xs text-gray-500 uppercase tracking-wider">
        {searchQuery 
          ? `${totalCount} rezultata za "${searchQuery}"` 
          : `${companies.length} od ${totalCount} firmi`}
        {totalPages > 1 && !searchQuery && (
          <span className="ml-2 text-gray-400">
            (str. {currentPage}/{totalPages})
          </span>
        )}
      </p>
      {companies.map((company) => (
        <CompanyListItem 
          key={company.id} 
          company={company} 
          onClick={() => onCompanyClick(company.id)} 
        />
      ))}
      {hasMore && (
        <div className="pt-2 pb-20 lg:pb-4">
          <button 
            onClick={onLoadMore} 
            disabled={loading}
            data-testid="load-more-button" 
            className="w-full lg:w-auto lg:px-6 py-3 lg:py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <span>Učitavanje...</span>
            ) : (
              <>
                <span>Učitaj još ({remainingCount > 0 ? remainingCount : 'više'})</span>
                <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
